import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useApolloClient, useQuery } from "react-apollo";

import { Field, Control, Input, Button, Label } from "rbx";
import moment from "moment";
import { OrdersTable } from "../../../../components/Orders";
import { UPDATE_ORDER_MUTATION } from "../../../../graphql/Orders/Orders.graphql";
import Loading from "../../../../components/Loading";
import { ALL_SYSTEM_CODES_QUERY } from "../../../../graphql/SystemCodes/SystemCodes.graphql";
import { ourToast as toast, runPromisesInSeries } from "../../../../utils";
import { useLocalStorage } from "../../../../hooks";

class VerifiedError extends Error {}

const Verified = ({ filters }) => {
  const client = useApolloClient();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [selectedFlatRows, setSelectedFlatRows] = useState([]);
  const [pickupDateStart, setPickupDateStart] = useLocalStorage(
    "ELD_VERIFIED_pickupDateStart",
    moment()
      .startOf("year")
      .format("YYYY-MM-DD")
  );

  const [pickupDateEnd, setPickupDateEnd] = useLocalStorage(
    "ELD_VERIFIED_pickupDateEnd",
    moment().format("YYYY-MM-DD")
  );

  const systemCodeStatus = useQuery(ALL_SYSTEM_CODES_QUERY, {
    variables: {
      where: {
        Category_in: ["Order", "ELD"],
        CodeName_in: ["OrderStatus", "ELDStatus"],
        CodeId_in: ["RV", "IMP", "IMD"]
      }
    }
  });
  const handleResetToImported = async selectedData => {
    try {
      if (!selectedData.length) {
        throw VerifiedError(
          "Please select some orders to be reset to imported"
        );
      }
      setLoading(true);
      const { SystemCodes } = systemCodeStatus.data;
      const ELDStatus = SystemCodes.find(s => s.CodeId === "IMD");
      const Status = SystemCodes.find(s => s.CodeId === "IMP");

      await runPromisesInSeries(
        selectedData.map((RecId, index) => {
          const obj = {
            mutation: UPDATE_ORDER_MUTATION,
            variables: {
              data: {
                StatusRecIdRef: Status.RecId,
                ELDStatusRecIdRef: ELDStatus.RecId,
                DateExported: new Date()
              },
              where: {
                RecId
              }
            }
          };
          if (index === selectedData.length - 1) {
            obj.refetchQueries = ["ALL_ORDERS_VIEW_QUERY"];
          }
          return () => client.mutate(obj);
        })
      );
      setLoading(false);
      toast.success("Orders reseted to imported successfully");
    } catch (err) {
      if (err instanceof VerifiedError) {
        toast.error(err.message);
      } else {
        toast.error("Error reseting Orders to Imported.");
      }
    }
  };

  const where = useMemo(() => {
    const isPickupDateStartValid = moment(pickupDateStart).isValid();
    const isPickupDateEndValid = moment(pickupDateEnd).isValid();

    if (isPickupDateStartValid && isPickupDateEndValid) {
      return {
        PickupDate_lt: moment(pickupDateEnd)
          .endOf("day")
          .toDate(),
        PickupDate_gt: moment(pickupDateStart)
          .startOf("day")
          .toDate()
      };
    }

    if (isPickupDateStartValid) {
      return {
        PickupDate_gt: moment(pickupDateStart)
          .startOf("day")
          .toDate()
      };
    }
    if (isPickupDateEndValid) {
      return {
        PickupDate_lt: moment(pickupDateEnd)
          .endOf("day")
          .toDate()
      };
    }

    return {};
  }, [pickupDateEnd, pickupDateStart]);

  if (loading || systemCodeStatus?.loading) {
    return <Loading />;
  }

  if (systemCodeStatus?.data?.SystemCodes.length > 0) {
    const { SystemCodes } = systemCodeStatus.data;
    const { RecId } = SystemCodes.find(s => s.CodeId === "RV");
    return (
      <OrdersTable
        callToAction1={handleResetToImported}
        initialSortBy={[
          { id: "PickupDate", desc: false },
          { id: "Trip", desc: false },
          {
            id: "Leg",
            desc: false
          }
        ]}
        pageHeadStart={
          <Field kind="group">
            <Control>
              <Label>Pickup Date Start</Label>
              <Input
                defaultValue={pickupDateStart}
                key={pickupDateStart}
                type="date"
                onBlur={({ target: { value } }) => {
                  setPickupDateStart(value);
                }}
              />
            </Control>
            <Control>
              <Label>Pickup Date End</Label>
              <Input
                defaultValue={pickupDateEnd}
                key={pickupDateEnd}
                type="date"
                onBlur={({ target: { value } }) => {
                  setPickupDateEnd(value);
                }}
              />
            </Control>
            <Control style={{ marginTop: "1.5rem" }}>
              <Button color="link">Filter</Button>
            </Control>
          </Field>
        }
        selectedFlatRows={selectedFlatRows}
        setSelectedFlatRows={setSelectedFlatRows}
        title="Route Verified"
        where={{
          ...filters,
          StatusRecIdRef: RecId,
          ...where
        }}
        onCellClick={cell => {
          if (cell.column.id !== "selection") {
            history.push(`/dispatch/orders/${cell.row.original.RecId}/summary`);
          }
        }}
      />
    );
  }
};
export default Verified;
